import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { Info, X, AlertTriangle, AlertCircle, Clock, CheckCircle, Pause, Play, Eye } from 'lucide-react';

const STATUS_DETAILS = {
  blocked: {
    icon: AlertCircle,
    description: 'Work is stopped due to an external dependency, issue, or blocker that must be resolved before progress can continue.',
    rule: 'A work item is Blocked when it has an active blocker issue or dependency that prevents any progress.'
  },
  behind: {
    icon: AlertTriangle,
    description: 'Work is progressing but is behind the planned schedule. The revised end date exceeds the original due date.',
    rule: 'A parent is Behind if ANY child has revised_end > parent planned_end.'
  },
  'due-at-risk': {
    icon: Clock,
    description: 'Work is on track but approaching its due date with insufficient buffer.',
    rule: 'Due Date at Risk when % complete is below 80% and due date is within 2 weeks.'
  },
  'in-progress': {
    icon: Play,
    description: 'Work is actively being done and progressing according to plan.',
    rule: 'In Progress when work has started (% complete > 0) and is not blocked, behind, or at risk.'
  },
  'in-review': {
    icon: Eye,
    description: 'Work is complete and awaiting review, approval, or sign-off.',
    rule: 'In Review when work is submitted for review but not yet approved.'
  },
  'not-started': {
    icon: Pause,
    description: 'Work has not yet begun. It is planned but no effort has been recorded.',
    rule: 'Not Started when % complete = 0 and work hasn\'t begun.'
  },
  completed: {
    icon: CheckCircle,
    description: 'Work is finished and meets all acceptance criteria.',
    rule: 'Completed when % complete = 100 and all child items are also completed.'
  }
};

const StatusLegend = ({ className = '', compact = false }) => {
  const [statuses, setStatuses] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);

  useEffect(() => {
    loadStatuses();
  }, []);

  const loadStatuses = async () => {
    try {
      const response = await api.getFilters();
      const sorted = response.data.statuses.sort((a, b) => a.severity - b.severity);
      setStatuses(sorted);
    } catch (error) {
      console.error('Failed to load statuses:', error);
    }
  };

  const handleStatusClick = (status) => {
    setSelectedStatus(status);
    setShowModal(true);
  };

  if (statuses.length === 0) return null;

  const displayStatuses = expanded ? statuses : statuses.slice(0, compact ? 7 : 4);

  if (compact) {
    return (
      <>
        <div className="flex items-center gap-2 flex-wrap">
          {displayStatuses.map((status) => (
            <button
              key={status.status}
              onClick={() => handleStatusClick(status)}
              className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-[var(--clr-surface-hover)] transition-colors"
              title={status.label}
              data-testid={`status-legend-${status.status}`}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: status.color_hex }}
              />
              <span className="text-xs text-[var(--clr-text-secondary)]">{status.label}</span>
            </button>
          ))}
          <button
            onClick={() => setShowModal(true)}
            className="p-1 text-[var(--clr-text-muted)] hover:text-[var(--clr-text-secondary)]"
            title="Learn about status meanings"
          >
            <Info className="w-4 h-4" />
          </button>
        </div>
        
        {showModal && (
          <StatusExplainerModal 
            statuses={statuses}
            selectedStatus={selectedStatus}
            onClose={() => { setShowModal(false); setSelectedStatus(null); }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className={`bg-[var(--clr-surface-card)] rounded-lg border border-gray-200 p-4 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
            <Info className="w-4 h-4 text-[var(--clr-text-muted)]" />
            Status Legend
          </h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowModal(true)}
              className="text-xs text-[var(--clr-brand-primary)] hover:underline"
            >
              Learn more
            </button>
            {statuses.length > 4 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-[var(--clr-text-muted)] hover:text-gray-700"
                data-testid="toggle-legend"
              >
                {expanded ? 'Show less' : `+${statuses.length - 4} more`}
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {displayStatuses.map((status) => (
            <button
              key={status.status}
              onClick={() => handleStatusClick(status)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors text-left"
              data-testid={`status-${status.status}`}
            >
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: status.color_hex }}
              />
              <span className="text-sm text-gray-700">{status.label}</span>
            </button>
          ))}
        </div>
      </div>
      
      {showModal && (
        <StatusExplainerModal 
          statuses={statuses}
          selectedStatus={selectedStatus}
          onClose={() => { setShowModal(false); setSelectedStatus(null); }}
        />
      )}
    </>
  );
};

const StatusExplainerModal = ({ statuses, selectedStatus, onClose }) => {
  const [activeStatus, setActiveStatus] = useState(selectedStatus?.status || statuses[0]?.status);
  
  const currentStatus = statuses.find(s => s.status === activeStatus);
  const details = STATUS_DETAILS[activeStatus?.toLowerCase()] || STATUS_DETAILS['in-progress'];
  const Icon = details?.icon || Info;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50" />
      <div 
        className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Status Definitions</h2>
          <button onClick={onClose} className="p-1 hover:bg-[var(--clr-surface-hover)] rounded">
            <X className="w-5 h-5 text-[var(--clr-text-muted)]" />
          </button>
        </div>
        
        <div className="flex min-h-[400px]">
          {/* Status List */}
          <div className="w-48 border-r bg-[var(--clr-surface-subtle)] p-2 overflow-y-auto">
            {statuses.map((status, idx) => (
              <button
                key={status.status}
                onClick={() => setActiveStatus(status.status)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left mb-1 transition-colors ${
                  activeStatus === status.status 
                    ? 'bg-white shadow-[var(--shadow-card)] border' 
                    : 'hover:bg-[var(--clr-surface-hover)]'
                }`}
              >
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: status.color_hex }}
                />
                <span className="text-sm text-gray-700 truncate">{status.label}</span>
              </button>
            ))}
          </div>
          
          {/* Status Details */}
          {currentStatus && (
            <div className="flex-1 p-6">
              <div className="flex items-center gap-4 mb-6">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: currentStatus.color_hex }}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[var(--clr-text-primary)]">{currentStatus.label}</h3>
                  <span 
                    className="inline-block px-2 py-0.5 rounded text-xs font-medium text-white"
                    style={{ backgroundColor: currentStatus.color_hex }}
                  >
                    Severity: {currentStatus.severity}
                  </span>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Description</h4>
                  <p className="text-sm text-[var(--clr-text-secondary)]">{details.description}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Calculation Rule</h4>
                  <div 
                    className="p-3 rounded-lg border"
                    style={{ backgroundColor: currentStatus.color_hex + '15', borderColor: currentStatus.color_hex + '30' }}
                  >
                    <p className="text-sm text-gray-700">{details.rule}</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Status Precedence</h4>
                  <p className="text-xs text-[var(--clr-text-muted)] mb-3">
                    When rolling up child statuses, the highest severity wins:
                  </p>
                  <div className="flex items-center gap-1 flex-wrap">
                    {statuses.map((s, idx) => (
                      <React.Fragment key={s.status}>
                        <span 
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            s.status === activeStatus ? 'text-white' : 'text-[var(--clr-text-secondary)]'
                          }`}
                          style={{ 
                            backgroundColor: s.status === activeStatus ? s.color_hex : '#f3f4f6'
                          }}
                        >
                          {s.label}
                        </span>
                        {idx < statuses.length - 1 && (
                          <span className="text-gray-300 text-xs">&gt;</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatusLegend;
